<template>
  <!--
    通用资源列表筛区面板：纯展示 + 事件上报，状态由 list.vue 持有。
    文档流与抽屉 overlay 各挂载一份，抽屉壳（高度/滚动/收起）由 list.vue 负责。
  -->
  <div class="linkListFilterPanel">
    <div class="commandDeck">
      <div class="commandDeck__zone commandDeck__zone--filters">
        <div class="filterDeckHeader">
          <div class="filterDeckHeader__leading">
            <span class="zoneHeader__title">筛选条件</span>
            <div v-if="activeFilterCount" class="filterChips">
              <span class="filterChip">已筛选 {{ activeFilterCount }} 项</span>
              <button
                type="button"
                class="filterChip filterChip--clear"
                @click="$emit('reset-and-search')"
              >
                清除
              </button>
            </div>
          </div>
          <div class="actionBar">
            <el-button
              class="actionButton actionButtonQuery"
              type="primary"
              size="small"
              icon="el-icon-search"
              @click="$emit('search')"
            >
              查询
            </el-button>
            <el-button
              class="actionButton actionButtonOutline"
              size="small"
              icon="el-icon-plus"
              @click="$emit('go-create')"
            >
              {{ profile === 'aiAssistant' ? '创建 Ai 角色' : '创建资源' }}
            </el-button>
            <el-dropdown trigger="click" @command="handleMoreAction">
              <el-button class="actionButton actionButtonGhost" size="small">
                更多操作<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown" class="genericResourceDropdown">
                <template v-if="profile === 'aiAssistant'">
                  <el-dropdown-item v-check-role command="batch-upload" icon="el-icon-folder-add">
                    Excel 批量导入
                  </el-dropdown-item>
                  <el-dropdown-item v-check-role command="batch-tag" icon="el-icon-price-tag">
                    批量打标
                  </el-dropdown-item>
                  <el-dropdown-item v-check-role command="batch-delete" icon="el-icon-delete">
                    批量删除
                  </el-dropdown-item>
                  <el-dropdown-item v-check-role command="import" icon="el-icon-document">
                    导入
                  </el-dropdown-item>
                  <el-dropdown-item command="export" icon="el-icon-download">
                    导出
                  </el-dropdown-item>
                </template>
                <template v-else>
                  <el-dropdown-item command="batch-upload" icon="el-icon-folder-add">
                    文件批量上传
                  </el-dropdown-item>
                  <el-dropdown-item v-check-role command="batch-delete" icon="el-icon-delete">
                    批量删除
                  </el-dropdown-item>
                  <el-dropdown-item v-check-role command="batch-tag" icon="el-icon-price-tag">
                    批量打标
                  </el-dropdown-item>
                  <el-dropdown-item v-check-role command="import" icon="el-icon-document">
                    导入
                  </el-dropdown-item>
                  <el-dropdown-item command="export" icon="el-icon-download">
                    导出
                  </el-dropdown-item>
                </template>
              </el-dropdown-menu>
            </el-dropdown>
            <el-button
              class="actionButton actionButtonGhost actionButton--reset"
              size="small"
              icon="el-icon-refresh-left"
              @click="$emit('reset-filters')"
            >
              重置
            </el-button>
          </div>
        </div>
        <div class="filterGrid">
          <div class="filterItem">
            <span class="searchText">资源名称</span>
            <el-input
              class="filterControl"
              placeholder="请输入资源名称"
              prefix-icon="el-icon-search"
              :value="resourceName"
              size="small"
              clearable
              @input="$emit('update:resourceName', $event)"
              @keydown.enter.native="$emit('search')"
            />
          </div>
          <div v-if="profile !== 'aiAssistant'" class="filterItem">
            <span class="searchText">资源 Key</span>
            <el-input
              class="filterControl filterControl--key"
              placeholder="请输入资源 Key"
              prefix-icon="el-icon-search"
              :value="filterKey"
              size="small"
              clearable
              @input="$emit('update:filterKey', $event)"
              @keydown.enter.native="$emit('search')"
            />
          </div>
          <div v-if="profile === 'aiAssistant'" class="filterItem">
            <span class="searchText">角色名称</span>
            <el-input
              class="filterControl"
              placeholder="请输入角色名称"
              prefix-icon="el-icon-search"
              :value="roleName"
              size="small"
              clearable
              @input="$emit('update:roleName', $event)"
              @keydown.enter.native="$emit('search')"
            />
          </div>
          <div v-if="profile !== 'aiAssistant'" class="filterItem">
            <span class="searchText">资源类型</span>
            <ResourceTypePicker
              class="filterControl"
              :value="assetType"
              :types="typeOptions"
              placeholder="请选择资源类型"
              size="small"
              @change="onAssetTypeChange"
            />
          </div>
          <div class="filterItem">
            <span class="searchText">{{ profile === 'aiAssistant' ? '资源分类' : '资源分类' }}</span>
            <el-select
              class="filterControl"
              popper-class="linkListFilterDropdown"
              :value="categoryType"
              placeholder="请选择资源分类"
              size="small"
              filterable
              clearable
              @change="onCategoryTypeChange"
            >
              <el-option
                v-for="item in categoryOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
          <div class="filterItem">
            <span class="searchText">资源状态</span>
            <el-select
              class="filterControl"
              popper-class="linkListFilterDropdown"
              :value="wallpaperStatus"
              placeholder="请选择资源状态"
              size="small"
              filterable
              clearable
              @change="onWallpaperStatusChange"
            >
              <el-option
                v-for="item in wallpaperStatusOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>
          <div class="filterItem">
            <span class="searchText">数据集</span>
            <el-select
              class="filterControl"
              popper-class="linkListFilterDropdown"
              :value="dataSetId"
              placeholder="请选择数据集"
              size="small"
              filterable
              clearable
              @change="onDataSetChange"
            >
              <el-option
                v-for="item in dataOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
        </div>
      </div>
    </div>
    <!-- 抽屉内可选条数摘要；文档流页内条数在 scrollControlBar__summary，避免双份垂直间距 -->
    <div
      v-if="showPanelSummary"
      class="filterPanelSummary"
    >
      <span class="filterPanelSummary__label">共</span>
      <strong class="filterPanelSummary__count">{{ panelTotalCount }}</strong>
      <span class="filterPanelSummary__label">条</span>
    </div>
  </div>
</template>

<script>
import ResourceTypePicker from "./ResourceTypePicker.vue";

/** 筛区 + 快捷操作内容区；布局壳由 list.vue 的文档流或 filterDrawerPanel 提供。 */
export default {
  name: "LinkListFilterPanel",
  components: { ResourceTypePicker },
  props: {
    resourceName: {
      type: String,
      default: "",
    },
    filterKey: {
      type: String,
      default: "",
    },
    assetType: {
      type: String,
      default: "",
    },
    categoryType: {
      type: [String, Number],
      default: "",
    },
    wallpaperStatus: {
      type: String,
      default: "",
    },
    dataSetId: {
      type: [String, Number],
      default: "",
    },
    activeFilterCount: {
      type: Number,
      default: 0,
    },
    /** 列表总条数；showPanelSummary 为 true 时在面板底展示（仅抽屉） */
    total: {
      type: Number,
      default: null,
    },
    /** 是否在面板底展示条数；文档流页内由 scrollControlBar 槽展示，避免重复占位 */
    showPanelSummary: {
      type: Boolean,
      default: false,
    },
    typeOptions: {
      type: Array,
      default: () => [],
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
    dataOptions: {
      type: Array,
      default: () => [],
    },
    wallpaperStatusOptions: {
      type: Array,
      default: () => [],
    },
    /** generic | aiAssistant：Ai 角色列表复用同一筛区壳，替换 Key/类型 为角色名 */
    profile: {
      type: String,
      default: 'generic',
    },
    roleName: {
      type: String,
      default: '',
    },
  },
  computed: {
    /** 与 list.vue listTotalCount 一致，0 也要展示 */
    panelTotalCount() {
      const parsed = Number(this.total);
      return Number.isFinite(parsed) ? parsed : 0;
    },
  },
  methods: {
    onAssetTypeChange(value) {
      this.$emit("update:assetType", value);
      this.$emit("search");
    },
    onCategoryTypeChange(value) {
      this.$emit("update:categoryType", value);
      this.$emit("search");
    },
    onWallpaperStatusChange(value) {
      this.$emit("update:wallpaperStatus", value);
      this.$emit("search");
    },
    onDataSetChange(value) {
      this.$emit("update:dataSetId", value);
      this.$emit("search");
    },
    /** 更多操作下拉：映射到 list.vue 既有事件，避免改父组件契约 */
    handleMoreAction(command) {
      const emitMap = {
        'batch-upload': 'open-batch-upload',
        'batch-tag': 'batch-tag',
        import: 'open-upload-excel',
        export: 'export-excel',
        'batch-delete': 'batch-delete',
      };
      const eventName = emitMap[command];
      if (eventName) {
        this.$emit(eventName);
      }
    },
  },
};
</script>

<style scoped>
@import "./linkListTheme.css";
@import "./linkActionButtons.css";

.linkListFilterPanel {
  padding: 0;
  background: transparent;
}

.filterPanelSummary {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--link-line, #eee9e2);
}

.filterPanelSummary__label {
  font-size: var(--link-text-sm);
  color: var(--link-ink-secondary);
}

.filterPanelSummary__count {
  font-size: var(--link-text-lg);
  font-weight: var(--link-font-semibold);
  color: var(--link-accent);
  font-variant-numeric: tabular-nums;
}

.filterGrid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px 10px;
}

.filterItem {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  align-items: center;
  column-gap: 8px;
}

.searchText {
  font-family: var(--link-font-family);
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
  /* 右对齐：短标签贴控件，避免固定 72px 左对齐产生大段空白 */
  text-align: right;
  white-space: nowrap;
}

.filterControl {
  width: 100%;
  max-width: 100%;
}

.filterControl--key ::v-deep .el-input__inner {
  font-family: var(--link-font-mono);
  font-size: var(--link-text-xs);
  letter-spacing: 0.01em;
}

.filterDeckHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.filterDeckHeader__leading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}

.actionBar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .filterGrid {
    grid-template-columns: 1fr;
  }

  .filterItem {
    grid-template-columns: 72px minmax(0, 1fr);
  }

  .filterDeckHeader {
    flex-direction: column;
    align-items: stretch;
  }

  .actionBar {
    justify-content: flex-start;
  }
}
</style>
